import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Archive, ArchiveRestore, Recycle, Skull } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useReference } from "@/lib/useReference";
import PageHeader, { EmptyState, schoolYear } from "@/components/PageHeader";
import Pagination from "@/components/Pagination";
import { PROGRAM_OPTIONS, PROGRAM_LABEL, detectProgram } from "@/lib/program";
import { PLAN_KEYS, PLAN_LABEL, PLAN_COLOR } from "@/lib/plans";

export default function Teams({ embedded = false }) {
  const { ref, reloadRef } = useReference();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", campus_id: "", district_id: "", year: 2026 });
  const [filterDistrict, setFilterDistrict] = useState("all");
  const [filterCampus, setFilterCampus] = useState("all");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [expungeTarget, setExpungeTarget] = useState(null);
  const [expungeText, setExpungeText] = useState("");

  const load = useCallback(async () => { const { data } = await api.get("/admin/teams"); setItems(data.items); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ name: "", code: "", campus_id: "", district_id: ref.districts[0]?.id || "", year: 2026, program_type: "", plan: "base" }); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ name: t.name, code: t.code, campus_id: t.campus_id, district_id: t.district_id, year: t.year, program_type: t.program_type || "", plan: t.plan || "custom" }); setOpen(true); };
  const programValue = form.program_type || detectProgram(form.name);

  const save = async () => {
    if (!form.campus_id) { toast.error("Select a campus"); return; }
    const payload = { ...form, year: parseInt(form.year, 10), program_type: programValue, plan: PLAN_KEYS.includes(form.plan) ? form.plan : undefined };
    try {
      if (editing) await api.put(`/admin/teams/${editing.id}`, payload);
      else await api.post("/admin/teams", payload);
      toast.success("Saved"); setOpen(false); load(); reloadRef();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleArchive = async (t) => {
    try { await api.post(`/admin/teams/${t.id}/archive?archived=${!t.archived}`); toast.success(t.archived ? "Unarchived" : "Archived"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openReplace = (t) => { setReplaceTarget(t); setConfirmText(""); };
  const doDeleteReplace = async () => {
    try {
      const { data } = await api.post(`/admin/teams/${replaceTarget.id}/delete-replace`);
      toast.success(`Team archived · ${data.deactivated} member(s)/coach(es) deactivated`);
      setReplaceTarget(null); setConfirmText("");
      load(); reloadRef();
      setEditing(null);
      setForm({ name: "", code: "", campus_id: data.campus_id, district_id: data.district_id, year: data.year, plan: "base" });
      setOpen(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openExpunge = (t) => { setExpungeTarget(t); setExpungeText(""); };
  const doExpunge = async () => {
    try {
      const { data } = await api.post(`/admin/teams/${expungeTarget.id}/expunge`);
      toast.success(`Team expunged · ${data.users_deleted} account(s) and all records permanently deleted`);
      setExpungeTarget(null); setExpungeText("");
      load(); reloadRef();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const campusesForDistrict = ref.campuses.filter((c) => c.district_id === form.district_id);
  const filterCampusOptions = ref.campuses.filter((c) => filterDistrict === "all" || c.district_id === filterDistrict);
  const shown = items
    .filter((t) => (filterDistrict === "all" || t.district_id === filterDistrict) && (filterCampus === "all" || t.campus_id === filterCampus))
    .sort((a, b) => (a.archived ? 1 : 0) - (b.archived ? 1 : 0));
  const pageItems = shown.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div>
      {embedded ? (
        <div className="mb-4 flex justify-end"><Button onClick={openNew} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="new-team-btn"><Plus className="mr-2 h-4 w-4" /> Add team</Button></div>
      ) : (
        <PageHeader title="Teams" subtitle="Manage teams by campus and year"
          actions={<Button onClick={openNew} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="new-team-btn"><Plus className="mr-2 h-4 w-4" /> Add team</Button>} />
      )}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">District</Label>
        <Select value={filterDistrict} onValueChange={(v) => { setFilterDistrict(v); setFilterCampus("all"); setPage(1); }}>
          <SelectTrigger className="w-52" data-testid="team-district-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All districts</SelectItem>
            {ref.districts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Label className="ml-2 text-xs font-bold uppercase tracking-wide text-slate-500">Campus</Label>
        <Select value={filterCampus} onValueChange={(v) => { setFilterCampus(v); setPage(1); }}>
          <SelectTrigger className="w-52" data-testid="team-campus-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All campuses</SelectItem>
            {filterCampusOptions.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {shown.length === 0 ? <EmptyState title="No teams" testId="teams-empty" /> : (
        <Card className="border-slate-200 shadow-sm overflow-x-auto">
          <Table data-testid="teams-table">
            <TableHeader><TableRow className="bg-slate-50">{["Team", "Code", "Program", "Licensed Plan", "Campus", "Year", "Status", ""].map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {pageItems.map((t) => (
                <TableRow key={t.id} data-testid={`team-row-${t.campus_code}-${t.code}`} className={t.archived ? "opacity-60" : ""}>
                  <TableCell className="font-semibold text-slate-800">{t.name}</TableCell>
                  <TableCell className="font-mono text-sm">{t.code}</TableCell>
                  <TableCell data-testid={`team-program-${t.campus_code}-${t.code}`}>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${t.program_type === "theatre" ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"}`}>{PROGRAM_LABEL[t.program_type] || "Spirit"}</span>
                  </TableCell>
                  <TableCell data-testid={`team-plan-${t.campus_code}-${t.code}`}>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${PLAN_COLOR[t.plan] || PLAN_COLOR.custom}`}>{PLAN_LABEL[t.plan] || "Custom"}</span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{t.campus_name} ({t.campus_code})</TableCell>
                  <TableCell className="tabular-nums">{schoolYear(t.year)}</TableCell>
                  <TableCell>{t.archived ? <span className="text-xs font-semibold text-slate-400">Archived</span> : <span className="text-xs font-semibold text-emerald-600">Active</span>}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(t)} data-testid={`edit-team-${t.campus_code}-${t.code}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => toggleArchive(t)} data-testid={`archive-team-${t.campus_code}-${t.code}`}>{t.archived ? <ArchiveRestore className="h-4 w-4 text-sky-500" /> : <Archive className="h-4 w-4 text-amber-500" />}</Button>
                    <Button size="icon" variant="ghost" title="Delete & Replace" onClick={() => openReplace(t)} data-testid={`replace-team-${t.campus_code}-${t.code}`}><Recycle className="h-4 w-4 text-red-500" /></Button>
                    <Button size="icon" variant="ghost" title="Expunge (permanent delete)" onClick={() => openExpunge(t)} data-testid={`expunge-team-${t.campus_code}-${t.code}`}><Skull className="h-4 w-4 text-slate-900" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      <Pagination page={page} pageSize={PAGE_SIZE} total={shown.length} onPage={setPage} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="team-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit team" : "Add team"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="team-name-input" /></div>
              <div className="space-y-2"><Label>Code</Label><Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} data-testid="team-code-input" placeholder="DNC/CHR/PEP" /></div>
            </div>
            <div className="space-y-2"><Label>District</Label>
              <Select value={form.district_id} onValueChange={(v) => setForm((f) => ({ ...f, district_id: v, campus_id: "" }))}>
                <SelectTrigger data-testid="team-district-select"><SelectValue placeholder="District" /></SelectTrigger>
                <SelectContent>{ref.districts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Campus</Label>
              <Select value={form.campus_id} onValueChange={(v) => setForm((f) => ({ ...f, campus_id: v }))}>
                <SelectTrigger data-testid="team-campus-select"><SelectValue placeholder="Campus" /></SelectTrigger>
                <SelectContent>{campusesForDistrict.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Year</Label><Input type="number" value={form.year} onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))} data-testid="team-year-input" /></div>
            <div className="space-y-2"><Label>Program type</Label>
              <Select value={programValue} onValueChange={(v) => setForm((f) => ({ ...f, program_type: v }))}>
                <SelectTrigger data-testid="team-program-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PROGRAM_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`team-program-option-${o.value}`}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Sets the example bio members see. {form.program_type ? "" : "Auto-detected from the team name — change it if needed."}</p>
            </div>
            <div className="space-y-2 border-t border-slate-100 pt-4"><Label>Licensed Plan</Label>
              <Select value={form.plan || "base"} onValueChange={(v) => setForm((f) => ({ ...f, plan: v }))}>
                <SelectTrigger data-testid="team-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLAN_KEYS.map((p) => <SelectItem key={p} value={p} data-testid={`team-plan-option-${p}`}>{PLAN_LABEL[p]}</SelectItem>)}
                  {form.plan === "custom" && <SelectItem value="custom" disabled>Custom (features set by hand)</SelectItem>}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500" data-testid="team-plan-hint">
                {editing && form.plan !== editing.plan && form.plan !== "custom"
                  ? `On save, this team's features switch to exactly the ${PLAN_LABEL[form.plan]} plan. Features turned on or off by hand are reset.`
                  : "Sets which features this team can use. Fine-tune them in Settings → License Management."}
              </p>
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="team-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!replaceTarget} onOpenChange={(o) => { if (!o) { setReplaceTarget(null); setConfirmText(""); } }}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="team-replace-dialog">
          <DialogHeader><DialogTitle className="font-heading text-red-600">Delete &amp; Replace team</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              This will <strong>archive “{replaceTarget?.name}”</strong> and <strong>deactivate every team member and coach</strong> assigned to it. Their merit, demerit and lettering records are kept for historical reporting. You'll then create a fresh replacement team on the same campus.
            </p>
            <div className="space-y-2">
              <Label>Type the team name <span className="font-mono font-semibold">{replaceTarget?.name}</span> to confirm</Label>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={replaceTarget?.name} data-testid="team-replace-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { setReplaceTarget(null); setConfirmText(""); }} data-testid="team-replace-cancel-btn">Cancel</Button>
            <Button disabled={confirmText !== replaceTarget?.name} onClick={doDeleteReplace} className="rounded-full bg-red-600 hover:bg-red-700" data-testid="team-replace-confirm-btn">Delete &amp; Replace</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!expungeTarget} onOpenChange={(o) => { if (!o) { setExpungeTarget(null); setExpungeText(""); } }}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="team-expunge-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-heading text-red-700"><Skull className="h-5 w-5" /> Expunge team</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <p className="font-semibold">⚠️ This cannot be undone.</p>
              <p className="mt-1">
                Expunging <strong>“{expungeTarget?.name}”</strong> will <strong>permanently delete</strong> the team and <strong>all</strong> of its data — every member and coach account, and all merit/demerit cases, lettering submissions, events and groups. Nothing is archived and no history is kept.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Type <span className="font-mono font-semibold">Expunge</span> to confirm</Label>
              <Input value={expungeText} onChange={(e) => setExpungeText(e.target.value)} placeholder="Expunge" data-testid="team-expunge-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { setExpungeTarget(null); setExpungeText(""); }} data-testid="team-expunge-cancel-btn">Cancel</Button>
            <Button disabled={expungeText !== "Expunge"} onClick={doExpunge} className="rounded-full bg-red-700 hover:bg-red-800" data-testid="team-expunge-confirm-btn"><Skull className="mr-2 h-4 w-4" /> Expunge permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
